import { useEffect, useState } from 'react';
import {
  AppendMessage,
  ThreadMessageLike,
  useExternalStoreRuntime,
} from '@assistant-ui/react';
import axios from 'axios';
import { useSendScoutAgentChat } from 'hooks/useScoutAgent';
import { trackScoutAgentInteraction } from 'utils/analytics';
import type {
  SavedSearchChange,
  Session,
  SessionEvent,
} from 'services/API/AIInteractionAPI';

const FALLBACK_NO_CONTENT_MESSAGE =
  "I'm sorry, I couldn't figure out your request on my end. Could you try again?";

const TIMEOUT_ERROR_MESSAGE =
  'Something’s taking longer than usual. Refresh to try again.';

const ERROR_MESSAGE = 'Something went wrong. Refresh your page, and try again.';

// `id` doubles as the feedback-correlation tag FeedbackControl sends back to
// ai-interaction/chat/{sessionId}/feedback/ as `event_timestamp` - for a
// historical event that's its real server timestamp; for a freshly-arrived
// turn there's no server timestamp yet, so a client-generated tag stands in
// (the backend treats it as an opaque string either way).
const sessionEventToMessage = (event: SessionEvent): ThreadMessageLike => ({
  id: event.timestamp,
  role: event.role === 'user' ? 'user' : 'assistant',
  content: [{ type: 'text', text: event.text?.join('\n\n') ?? '' }],
});

// Empty content + running status renders a synthetic empty "running" text
// part, which ChatView's Text part component treats as a cue to show the
// loading indicator.
const createRunningMessage = (): ThreadMessageLike => ({
  role: 'assistant',
  content: [],
  status: { type: 'running' },
});

/**
 * Wires the existing Scout chat data flow (Session/SessionEvent, the
 * poll-to-completion mutation) into an assistant-ui ExternalStoreRuntime, so
 * <ChatView> can render it with assistant-ui's Thread/Message primitives
 * instead of hand-rolled message state.
 *
 * `isAwaitingResponse` covers a case `onNew` below can't: a brand-new chat's
 * first message is sent by the page that creates the session (see
 * ScoutAgent.tsx's handlePromptSubmit), before this hook - or any session id
 * - exists, so there's no onNew call to append a local running placeholder
 * to. The caller passes its own pending-mutation flag instead, and a
 * placeholder is derived here whenever the newest known message is the
 * user's with nothing answering it yet.
 */
export const useScoutChatRuntime = (
  chat: Session,
  onSavedSearchChange?: (changes: SavedSearchChange[]) => void,
  isAwaitingResponse?: boolean,
) => {
  const [messages, setMessages] = useState<readonly ThreadMessageLike[]>(() =>
    chat.events.map(sessionEventToMessage),
  );
  const sendChat = useSendScoutAgentChat();

  // Resync only when switching to a different chat. `chat` is only ever
  // passed in already-loaded (see useSavedSearchChatSession), so the initial
  // useState above already seeds this session's history - this effect exists
  // for switching sessions, not for re-syncing within one. Re-running on
  // every `chat.events` change would also fire after each sent message: the
  // send mutation invalidates and refetches this same session query in the
  // background (see useSendScoutAgentChat's onSuccess), and that refetch's
  // plain-text server history would clobber the richer local message we just
  // appended in onNew (e.g. one carrying a saved-search-change data part).
  useEffect(() => {
    setMessages(chat.events.map(sessionEventToMessage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.session_id]);

  const onNew = async (message: AppendMessage) => {
    if (message.content.length !== 1 || message.content[0]?.type !== 'text') {
      throw new Error('Only text messages are supported');
    }
    const { text } = message.content[0];

    trackScoutAgentInteraction({
      clickText: 'Send message',
      clickAction: 'send_message',
      scoutAgentChatId: chat.session_id,
    });

    const userMessage: ThreadMessageLike = {
      role: 'user',
      content: [{ type: 'text', text }],
    };
    setMessages((prev) => [...prev, userMessage, createRunningMessage()]);

    try {
      const data = await sendChat.mutateAsync({
        message: text,
        sessionId: chat.session_id,
      });
      const agentText =
        data?.agent_text?.length > 0
          ? data.agent_text.join('\n\n')
          : FALLBACK_NO_CONTENT_MESSAGE;
      const savedSearchChanges = data?.saved_search_changes ?? [];

      const assistantMessage: ThreadMessageLike = {
        id: `response-${Date.now()}`,
        role: 'assistant',
        content: [
          { type: 'text', text: agentText },
          ...savedSearchChanges.map((change) => ({
            type: 'data' as const,
            name: 'saved-search-change',
            data: change,
          })),
        ],
      };
      setMessages((prev) => [...prev.slice(0, -1), assistantMessage]);

      if (savedSearchChanges.length) {
        onSavedSearchChange?.(savedSearchChanges);
      }
    } catch (error) {
      const is504 =
        (axios.isAxiosError(error) && error.response?.status === 504) ||
        (error instanceof Error && error.message === '504');
      const errorText = is504 ? TIMEOUT_ERROR_MESSAGE : ERROR_MESSAGE;
      const errorMessage: ThreadMessageLike = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: [{ type: 'text', text: errorText }],
      };
      setMessages((prev) => [...prev.slice(0, -1), errorMessage]);
    }
  };

  const lastMessage = messages[messages.length - 1];
  const runtimeMessages =
    isAwaitingResponse && lastMessage?.role === 'user'
      ? [...messages, createRunningMessage()]
      : messages;

  return useExternalStoreRuntime<ThreadMessageLike>({
    messages: runtimeMessages,
    setMessages,
    onNew,
    convertMessage: (message) => message,
  });
};
